import { providers } from 'near-api-js';

// wallet selector UI
import '@near-wallet-selector/modal-ui/styles.css';
import { setupModal } from '@near-wallet-selector/modal-ui';

// Icons
import LedgerIconUrl from '@near-wallet-selector/ledger/assets/ledger-icon.png';
import NearIconUrl from '@near-wallet-selector/near-wallet/assets/near-wallet-icon.png';
import MyNearIconUrl from '@near-wallet-selector/my-near-wallet/assets/my-near-wallet-icon.png';
import HereWalletIconUrl from '@near-wallet-selector/here-wallet/assets/here-wallet-icon.png';
import MeteorWalletIconUrl from '@near-wallet-selector/meteor-wallet/assets/meteor-icon.png';
import SenderIconUrl from '@near-wallet-selector/sender/assets/sender-icon.png';
import NightlyIconUrl from '@near-wallet-selector/nightly/assets/nightly.png';

// wallet selector options
import { setupWalletSelector } from '@near-wallet-selector/core';
import { setupLedger } from '@near-wallet-selector/ledger';
import { setupNearWallet } from '@near-wallet-selector/near-wallet';
import { setupMeteorWallet } from '@near-wallet-selector/meteor-wallet';
import { setupSender } from '@near-wallet-selector/sender';
import { setupNightly } from "@near-wallet-selector/nightly";
import { setupHereWallet } from '@near-wallet-selector/here-wallet';
import { setupMyNearWallet } from "@near-wallet-selector/my-near-wallet";

const THIRTY_TGAS = '30000000000000';
const NO_DEPOSIT = '0';

const MAINNET_RPC_ENDPOINTS = [
  "https://free.rpc.fastnear.com",
  "https://near.lava.build",
  "https://rpc.mainnet.near.org",
];

/**
 * Try an RPC call across multiple endpoints with failover.
 * @param {string} networkId - "mainnet" or "testnet"
 * @param {string} defaultUrl - the default nodeUrl from wallet selector
 * @param {Function} callFn - receives a JsonRpcProvider, returns a promise
 */
const rpcWithFailover = async (networkId, defaultUrl, callFn) => {
  const endpoints = networkId === "mainnet" ? MAINNET_RPC_ENDPOINTS : [defaultUrl];
  let lastError;
  for (const url of endpoints) {
    try {
      const provider = new providers.JsonRpcProvider({ url });
      return await callFn(provider);
    } catch (error) {
      lastError = error;
      const msg = error.message || "";
      if (msg.includes("429") || msg.includes("timeout") || msg.includes("ECONNRE")) {
        continue; // try next endpoint
      }
      throw error; // non-retryable, rethrow immediately
    }
  }
  throw lastError;
};

// Wallet that simplifies using the wallet selector
export class Wallet {
  accountId = undefined;
  walletSelector = undefined;
  interface = undefined;
  network;
  createAccessKeyFor;

  constructor({createAccessKeyFor = undefined, network}) {
    this.createAccessKeyFor = createAccessKeyFor;
    this.network = network;
  }

  // To be called when the website loads
  async startUp() {
    this.walletSelector = await setupWalletSelector({
      network: this.network,
      modules: [
        setupNearWallet({iconUrl: NearIconUrl}),
        setupMyNearWallet({iconUrl: MyNearIconUrl}),
        setupMeteorWallet({iconUrl: MeteorWalletIconUrl}),
        setupNightly({iconUrl: NightlyIconUrl}),
        setupHereWallet({iconUrl: HereWalletIconUrl}),
        setupSender({iconUrl: SenderIconUrl}),
        setupLedger({iconUrl: LedgerIconUrl}),
      ],
    });

    const isSignedIn = this.walletSelector.isSignedIn();
    if (isSignedIn) {
      this.interface = await this.walletSelector.wallet();
      this.accountId = this.walletSelector.store.getState().accounts[0].accountId;
    }

    return isSignedIn;
  }

  async onAccountChange(accountId) {
    this.interface = await this.walletSelector?.wallet();
    this.accountId = accountId;
  }

  // Sign-in method
  signIn() {
    const description = 'Please select a wallet to sign in.';
    const modal = setupModal(this.walletSelector, {
      contractId: this.createAccessKeyFor,
      description
    });
    modal.show();
  }

  // Sign-out method
  signOut() {
    if (!this?.interface) return;
    this.interface.signOut();

    if (this.interface.id === "near-wallet" || this.interface.id === "my-near-wallet") {
      this.interface = this.accountId = this.createAccessKeyFor = undefined;
      window.location.replace(window.location.origin + window.location.pathname);
    }
  }

  // Make a read-only call to retrieve information from the network
  async viewMethod({contractId, method, args = {}}) {
    if (!this.walletSelector) return;

    const {network} = this.walletSelector.options;
    const res = await rpcWithFailover(network.networkId, network.nodeUrl, (provider) =>
      provider.query({
        request_type: 'call_function',
        account_id: contractId,
        method_name: method,
        args_base64: Buffer.from(JSON.stringify(args)).toString('base64'),
        finality: 'optimistic',
      })
    );
    return JSON.parse(Buffer.from(res.result).toString());
  }

  // Call a method that changes the contract's state
  async callMethod({
    contractId, method, args = {}, gas = THIRTY_TGAS, deposit = NO_DEPOSIT
  }) {
    // Sign a transaction with the "FunctionCall" action
    if (!this.interface) return;

    const tx = await this.interface.signAndSendTransaction({
      signerId: this.accountId,
      receiverId: contractId,
      actions: [
        {
          type: 'FunctionCall',
          params: {
            methodName: method,
            args,
            gas,
            deposit,
          },
        },
      ],
    });

    return this.getTransactionResult(tx.transaction.hash);
  }

  async checkAccountAvailable(accountId) {
    const {network} = this.walletSelector.options;
    return rpcWithFailover(network.networkId, network.nodeUrl, (provider) =>
      provider.query({
        request_type: "view_account",
        finality: 'optimistic',
        account_id: accountId,
      })
    );
  }

  // Get transaction result from the network
  async getTransactionResult(txHash) {
    if (!this.walletSelector) return;

    const {network} = this.walletSelector.options;
    const transaction = await rpcWithFailover(network.networkId, network.nodeUrl, (provider) =>
      provider.txStatus(txHash, 'unnused')
    );
    return providers.getTransactionLastResult(transaction);
  }

}